<template>
	<div class="member-points-mask" @click.self="close">
		<section class="member-points-popup" role="dialog" aria-modal="true" :aria-labelledby="titleId">
			<button class="member-points-close" type="button" :aria-label="$t('member_popup.close')" @click="close">
				<i class="fa fa-close" aria-hidden="true"></i>
			</button>

			<div class="member-points-badge" aria-hidden="true">
				<span>VIP</span>
				<strong>{{ level }}</strong>
			</div>

			<h2 :id="titleId">{{ $t('member_popup.title') }}</h2>
			<p class="member-points-message">
				{{ $t('member_popup.message', {
					points: formatNumber(earnedPoints),
					nextLevel,
					remaining: formatNumber(remainingPoints)
				}) }}
			</p>

			<div class="member-points-progress-card">
				<div class="member-points-progress-head">
					<strong>{{ $t('member_popup.progress', {
						current: formatNumber(currentPoints),
						target: formatNumber(nextLevelPoints)
					}) }}</strong>
					<span>VIP{{ level }}</span>
				</div>
				<div class="member-points-progress" role="progressbar" :aria-valuenow="currentPoints"
					aria-valuemin="0" :aria-valuemax="nextLevelPoints">
					<span :style="{ width: progressPercent + '%' }"></span>
				</div>
				<p>{{ $t('member_popup.next', {
					nextLevel,
					remaining: formatNumber(remainingPoints)
				}) }}</p>
			</div>

			<label class="member-points-suppress">
				<input v-model="suppressForSevenDays" type="checkbox">
				<span>{{ $t('member_popup.suppress') }}</span>
			</label>

			<div class="member-points-actions">
				<button class="member-points-later" type="button" @click="close">
					{{ $t('member_popup.later') }}
				</button>
				<button class="member-points-rewards" type="button" @click="close">
					{{ $t('member_popup.rewards') }}
				</button>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	name: 'MemberPointsPopup',
	props: {
		level: { type: Number, default: 4 },
		nextLevel: { type: Number, default: 5 },
		earnedPoints: { type: Number, default: 30 },
		currentPoints: { type: Number, default: 15240 },
		nextLevelPoints: { type: Number, default: 30000 },
		remainingPoints: { type: Number, default: 14760 }
	},
	data() {
		return {
			suppressForSevenDays: false,
			titleId: `member-points-popup-title-${this._uid}`
		}
	},
	computed: {
		progressPercent() {
			const target = Math.max(1, Number(this.nextLevelPoints) || 1)
			return Math.min(100, Math.max(0, (Number(this.currentPoints) || 0) / target * 100))
		}
	},
	methods: {
		formatNumber(value) {
			const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale.replace('_', '-') : 'zh-TW'
			return new Intl.NumberFormat(locale).format(Math.max(0, Number(value) || 0))
		},
		close() {
			this.$emit('close')
		}
	}
}
</script>

<style scoped>
.member-points-mask {
	position: fixed;
	inset: 0;
	z-index: 10020;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	padding: 20px;
	background-color: rgba(29, 25, 22, .62);
}

.member-points-popup {
	position: relative;
	box-sizing: border-box;
	width: 430px;
	max-width: 100%;
	padding: 24px 24px 22px;
	border: 1px solid #efbd68;
	border-radius: 7px;
	background-color: #fffefb;
	box-shadow: 0 18px 44px rgba(33, 25, 18, .28);
	color: #2e2a27;
	text-align: center;
}

.member-points-close {
	position: absolute;
	top: 10px;
	right: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background-color: #f5f3ef;
	color: #88817b;
	font-size: 17px;
	cursor: pointer;
}

.member-points-close:hover { background-color: #ece8e2; color: #4f4944; }

.member-points-badge {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	width: 76px;
	height: 76px;
	margin: -2px auto 12px;
	border: 4px solid #8a6121;
	border-radius: 50%;
	background-color: #ffd46a;
	box-shadow: inset 0 0 0 2px #ffe8a4, 0 3px 8px rgba(112, 75, 25, .2);
	color: #70470f;
	line-height: 1;
}

.member-points-badge span { margin-bottom: 3px; font-size: 14px; }
.member-points-badge strong { font-size: 25px; }
.member-points-popup h2 { margin: 0; font-size: 22px; line-height: 30px; }
.member-points-message { margin: 12px auto 16px; color: #77706b; font-size: 14px; line-height: 1.7; }

.member-points-progress-card {
	padding: 12px;
	border: 1px solid #f0ca85;
	border-radius: 6px;
	background-color: #fff9ec;
}

.member-points-progress-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	color: #6d645d;
	font-size: 12px;
	line-height: 18px;
	text-align: left;
}

.member-points-progress-head strong { min-width: 0; overflow-wrap: anywhere; }
.member-points-progress-head span { flex: 0 0 auto; color: #a26c25; }

.member-points-progress {
	width: 100%;
	height: 9px;
	margin-top: 7px;
	overflow: hidden;
	border-radius: 5px;
	background-color: #e9e3dc;
	box-shadow: inset 0 1px 2px rgba(80, 57, 35, .1);
}

.member-points-progress span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background-color: var(--theme-color, #f6aa50);
}

.member-points-progress-card p { margin: 8px 0 0; color: #716860; font-size: 12px; line-height: 18px; }

.member-points-suppress {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-top: 18px;
	color: #6f6862;
	font-size: 13px;
	cursor: pointer;
}

.member-points-suppress input {
	width: 17px;
	height: 17px;
	margin: 0;
	accent-color: var(--theme-color, #f6aa50);
}

.member-points-actions { display: flex; justify-content: center; gap: 10px; margin-top: 18px; }
.member-points-actions button { min-width: 112px; height: 42px; padding: 0 18px; border-radius: 21px; font-size: 14px; font-weight: 700; cursor: pointer; }
.member-points-later { border: 1px solid #d8d2cd; background-color: #fff; color: #5f5852; }
.member-points-rewards { border: 1px solid var(--theme-color, #f6aa50); background-color: var(--theme-color, #f6aa50); color: #fff; box-shadow: 0 5px 12px rgba(203, 115, 38, .2); }
.member-points-later:hover { border-color: #bab2ac; background-color: #faf9f7; }
.member-points-rewards:hover { filter: brightness(.96); }
.member-points-actions button:focus-visible, .member-points-close:focus-visible, .member-points-suppress input:focus-visible { outline: 2px solid #db8152; outline-offset: 2px; }

@media (max-width: 480px) {
	.member-points-mask { padding: 12px; }
	.member-points-popup { padding: 22px 16px 18px; }
	.member-points-popup h2 { padding: 0 28px; font-size: 19px; line-height: 27px; }
	.member-points-actions { flex-direction: column-reverse; }
	.member-points-actions button { width: 100%; }
}
</style>
